import api from '../api.js';
import db from '../../versa/db.js';
import crud from '../../versa/crud.js';
import router from '../../versa/router.js';
import formset from '../../versa/formset.js';
import record from '../../versa/record.js';
import { flatten, deepen } from '../../versa/record.js';
import { notify } from '../../versa/msgbox.js';
import pdf from '../../versa/pdf.js';
import view from '../views/customers.js';
import form from '../forms/customers.js';
import print_report_form from '../forms/customers-print-report.js';
import * as types from '../types.js';

const customers = function()
{

	let customers_crud = crud.create({
		async loadBaseData()
		{
			api.call(api.list_companies, this.queryAll()).then((data) => db.set('companies', data).catch(error => notify(error)));
			api.call(api.list_payment_terms, this.queryAll()).then((data) => db.set('payment_terms', data).catch(error => notify(error)));
			api.call(api.list_price_lists, this.queryAll()).then((data) => db.set('price_lists', data).catch(error => notify(error)));
			api.call(api.list_customer_routes, this.queryAll()).then((data) => db.set('customer_routes', data).catch(error => notify(error)));
			api.call(api.list_cities, this.queryAll()).then((data) => db.set('cities', data).catch(error => notify(error)));
		},
		async queryData(query)
		{
			return await api.call(api.list_customers, query);
		},
		async loadRecord(id)
		{
			let _record = await api.call(api.select_customer, ...JSON.parse(id));
			_record = flatten(_record, 'billing_address');
			_record = flatten(_record, 'delivery_address');
			if (_record.billing_address_city_id) {
				const c = await db.get('cities', _record.billing_address_city_id);
				if (c) {
					_record['billing_address_state'] = c.state;
					_record['billing_address_city_id'] = c.city;
				}
			}
			if (_record.delivery_address_city_id) {
				const c = await db.get('cities', _record.delivery_address_city_id);
				if (c) {
					_record['delivery_address_state'] = c.state;
					_record['delivery_address_city_id'] = c.city;
				}
			}

			if (_record.route_id) {
				const key = [_record.company_id, _record.route_id];
				const existing = await db.get('customer_routes', key).catch(() => null);
				if (!existing) {
					await db.set('customer_routes', [{
						company_id: _record.company_id,
						id: _record.route_id,
						code: _record.route_code || String(_record.route_id),
						name: _record.route_name || '',
						is_disabled: true,
					}]).catch(() => {});
				}
			}
			return _record;
		},
		async saveRecord(data, old_data)
		{
			const sel = '#customer';
			const normalize = s => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
			const resolveCityId = async (state, cityVal) => {
				if (!state || cityVal == null || cityVal === '') return cityVal;
				const v = String(cityVal).trim();
				if (/^\d+$/.test(v)) return v;
				const range = IDBKeyRange.bound([state, ''], [state, '\uffff']);
				const cities = await db.list('cities', range, 2).catch(() => []);
				const c = (cities || []).find(x => normalize(x?.city || '') === normalize(v));
				return c ? c.id : cityVal;
			};
			const getStateFromDom = (prefix) => {
				const el = document.querySelector(`${sel} #${prefix}_address_state`);
				return el?.value ? String(el.value).trim().toUpperCase().slice(0, 2) : '';
			};
			const ensureAddress = (addr, copyFrom) => {
				const base = copyFrom || {};
				const o = {
					id: addr?.id ?? base.id,
					street: String(addr?.street ?? base.street ?? '').trim(),
					number: String(addr?.number ?? base.number ?? '').trim(),
					complement: String(addr?.complement ?? base.complement ?? '').trim() || null,
					district: String(addr?.district ?? base.district ?? '').trim(),
					zipcode: String(addr?.zipcode ?? base.zipcode ?? '').replace(/\D/g, ''),
					city_id: addr?.city_id ?? base.city_id ?? null,
					state: String(addr?.state ?? base.state ?? '').trim().toUpperCase().slice(0, 2) || null
				};
				if (!o.state && o.city_id && !/^\d{7}$/.test(String(o.city_id)))
					o.state = getStateFromDom('delivery') || getStateFromDom('billing');
				const hasData = o.street || o.number || o.district || o.zipcode || o.city_id;
				if (!hasData) return null;
				if (o.id != null) o.id = Number(o.id);
				return o;
			};
			const sameAddress = document.querySelector(`${sel} #same_billing_delivery`)?.checked;
			if (data.billing_address_state && data.billing_address_city_id)
				data.billing_address_city_id = await resolveCityId(data.billing_address_state, data.billing_address_city_id);
			if (data.delivery_address_state && data.delivery_address_city_id)
				data.delivery_address_city_id = await resolveCityId(data.delivery_address_state, data.delivery_address_city_id);
			const getZipFromDom = (prefix) => {
				const el = document.querySelector(`${sel} #${prefix}_address_zipcode`);
				return el?.value ? String(el.value).replace(/\D/g, '') : '';
			};
			Object.keys(data).forEach(key =>
			{
				let parts = key.match(/^((?:billing|delivery)_address)_(\w+)$/);
				if (!parts)
					return;
				if (!data[parts[1]])
					data[parts[1]] = {};
				data[parts[1]][parts[2]] = data[key];
				delete data[key];
			});
			let billing = data.billing_address || {};
			let delivery = data.delivery_address || {};
			if (!delivery.zipcode || String(delivery.zipcode).replace(/\D/g, '') === '')
				delivery.zipcode = getZipFromDom('delivery');
			if (!billing.zipcode || String(billing.zipcode).replace(/\D/g, '') === '') {
				billing.zipcode = document.querySelector(`${sel} #same_billing_delivery`)?.checked
					? getZipFromDom('delivery') : getZipFromDom('billing');
			}
			if (sameAddress) {
				const keys = ['street', 'number', 'complement', 'district', 'zipcode', 'city_id', 'state'];
				const merged = {};
				keys.forEach(k => {
					const vb = delivery?.[k];
					const va = billing?.[k];
					merged[k] = (vb != null && String(vb).trim() !== '') ? vb : (va != null && String(va).trim() !== '') ? va : (va ?? vb);
				});
				merged.id = billing?.id ?? delivery?.id;
				billing = merged;
				delivery = merged;
			}
			const billAddr = ensureAddress(billing);
			const delAddr = ensureAddress(delivery);
			data.billing_address = billAddr;
			data.delivery_address = delAddr;
			let company_id = data.company_id;
			let id = data.id;
			delete data['company_id'];
			delete data['id'];
			data.delivery_term = Number(data.delivery_term);
			const isLocal = data.type === 'L' || data.type?.toUpperCase?.() === 'L';
			const pt = Number(data.payment_term_id);
			data.payment_term_id = (isLocal || !data.payment_term_id || data.payment_term_id === '' || isNaN(pt) || pt < 1) ? null : pt;
			data.payment_method = data.payment_method ? String(data.payment_method).trim() : null;
			const pl = Number(data.price_list_id);
			data.price_list_id = (!data.price_list_id || data.price_list_id === '' || isNaN(pl) || pl < 1) ? null : pl;
			const ri = Number(data.route_id);
			data.route_id = (!data.route_id || data.route_id === '' || isNaN(ri) || ri < 1) ? null : ri;
			data.is_end_consumer = data.is_end_consumer == '1';		// Can be: undefined, '0', '1'
			if (old_data)
				return await api.call(api.update_customer, company_id, id, data);
			else
				return await api.call(api.insert_customer, company_id, data);
		},
	});
	
	// Index = position in the backend's SELECT
	let fieldspecs = [
		{ index: 1, field: 'company_id', label: 'Empresa', types: [types.datalist()], placeholder: '', title: 'Empresa', store: 'companies', labels: ['nick'] },
		{ index: 2, field: 'id', label: 'Cliente', types: [types.number(0, 2)], placeholder: '0', title: 'Identificador do cliente' },
		{ index: 3, field: 'type', label: 'Tipo', types: [types.opt_person_type()], placeholder: '', title: 'Tipo de personalidade jurídica' },
		{ index: 4, field: 'nick', label: 'Apelido', types: [types.datalist()], placeholder: '', title: 'Nome curto do cliente' },
		{ index: 5, field: 'name', label: 'Nome', types: [], placeholder: '', title: 'Nome ou razão social' },
		{ index: 6, field: 'cpf', label: 'CPF', types: [types.cpf()], placeholder: '000.000.000-00', title: 'Número do CPF' },
		{ index: 7, field: 'cnpj', label: 'CNPJ', types: [types.cnpj()], placeholder: '00.000.000/0000-00', title: 'Número do CNPJ' },
		{ index: 8, field: 'ie', label: 'IE', types: [types.ie()], placeholder: '000.000.000', title: 'Inscrição Estadual' },
		{ index: 9, field: 'ie_type', label: 'Tipo IE', types: [types.opt_ie_type(), types.datalist()], placeholder: '', title: 'Tipo de Inscrição Estadual' },
		{ index: 10, field: 'phone', label: 'Fone', types: [types.phone()], placeholder: '+00 (00) 0000-0000', title: 'Telefone' },
		{ index: 11, field: 'email', label: 'Email', types: [types.email()], placeholder: 'abc@xyz.com', title: 'Email' },
		{ index: 14, field: 'delivery_term', label: 'Prazo Entrega', types: [types.number(0, 2)], placeholder: '', title: 'Prazo de entrega em dias' },
		{ index: 15, field: 'payment_term_id', label: 'Condição Pagto', types: [types.datalist()], placeholder: '', title: 'Condição de pagamento', store: 'payment_terms', labels: ['name'] },
		{ index: 16, field: 'payment_method', label: 'Pagamento', types: [types.opt_payment_method(), types.datalist()], placeholder: '', title: 'Forma de pagamento padrão' },
		{ index: 17, field: 'price_list_id', label: 'Lista Preços', types: [types.datalist()], placeholder: '', title: 'Lista de preços vinculada ao cliente', store: 'price_lists', labels: ['name'] },
		{ index: 18, field: 'route_id', label: 'Rota', types: [types.datalist()], placeholder: '', title: 'Rota de entrega do cliente', store: 'customer_routes', labels: ['code', 'name'] },
		{ index: 21, field: 'is_end_consumer', label: 'Consumidor', types: [types.opt_bool(), types.datalist()], placeholder: '', title: 'Indicador de consumidor final' },
	];

	let reports = [
		{ hash: '#print-customers-report', label: 'Relatório&nbsp;de&nbsp;Clientes' }
	];

	async function buildFilter(data) {
		const parts = [];
		if (data.company_id) {
			const c = await db.get('companies', data.company_id) || await db.get('companies', [data.company_id]);
			const id = c?.id ?? data.company_id;
			if (id) parts.push(`company_id*eq*${id}`);
		}
		if (data.billing_state) parts.push(`billing_state*eq*${String(data.billing_state).trim().toUpperCase().slice(0, 2)}`);
		if (data.type) parts.push(`type*eq*${String(data.type).trim().toUpperCase().slice(0, 1)}`);
		if (data.ie_type) parts.push(`ie_type*eq*${data.ie_type}`);
		if (data.is_end_consumer !== '' && data.is_end_consumer != null) parts.push(`is_end_consumer*eq*${data.is_end_consumer}`);
		return parts.join(',');
	}

	async function updateCountMsg(formData) {
		const filter = await buildFilter(formData || {});
		const resp = await api.call(api.count_customers_report, { filter });
		const count = typeof resp === 'number' ? resp : parseInt(resp, 10) || 0;
		const el = document.querySelector('#count-msg');
		if (el) el.textContent = `${count} registro(s) serão impressos.`;
	}

	function downloadFile(base64Content, filename, mimeType) {
		const bin = atob(base64Content);
		const arr = new Uint8Array(bin.length);
		for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
		const blob = new Blob([arr], { type: mimeType });
		const a = document.createElement('a');
		a.href = URL.createObjectURL(blob);
		a.download = filename || 'relatorio';
		a.click();
		URL.revokeObjectURL(a.href);
	}

	async function doPrint(data) {
		try {
			const filter = await buildFilter(data);
			const format = (data.format || 'pdf').toLowerCase();
			const resp = await api.call(api.print_customers_report, { filter, format });
			if (resp?.pdf) pdf(resp.pdf);
			else if (resp?.csv) {
				downloadFile(resp.csv, resp.filename || 'clientes.csv', 'text/csv;charset=utf-8');
				notify('Download iniciado.', 'Sucesso');
			} else if (resp?.xlsx) {
				downloadFile(resp.xlsx, resp.filename || 'clientes.xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
				notify('Download iniciado.', 'Sucesso');
			} else notify('Nenhum dado para o relatório.', 'Aviso');
		} catch (e) {
			notify(e?.message || String(e), 'Erro');
		}
	}

	router.set('#print-customers-report', () => {
		const dlg = record.edit(print_report_form, async (data) => {
			await doPrint(data);
			dlg.destroy();
		}, {}, false);
		const fs = dlg;
		const formEl = document.querySelector(print_report_form.form.selector);
		if (formEl) {
			formEl.addEventListener('change', () => updateCountMsg(fs.form?.getValues?.()));
			formEl.addEventListener('input', () => updateCountMsg(fs.form?.getValues?.()));
		}
		setTimeout(() => updateCountMsg(fs.form?.getValues?.()), 200);
	});

	customers_crud.run(
		'customers',
		'Clientes',
		document.getElementById('workarea'),
		view,
		form,
		fieldspecs,
		[],
		reports
	);

};

export default customers;

